/**
 * Ant Design 5 theme configuration for the glass design system.
 *
 * These tokens are the *first* line of theming: getting radii, colours,
 * shadows and container backgrounds right here means Ant renders close to the
 * target look natively, and the CSS layer in assets/glass/ only has to add the
 * things tokens can't express (backdrop-filter, gradients, inset highlights).
 *
 * Keep the values here in step with assets/glass/_tokens.scss.
 */

import { theme as antdThemeAlgorithms } from 'antd';

const BRAND = '#552b9d';

// Surfaces/text that differ between light and dark — everything else (radii,
// brand/semantic colours, motion) is shared and stays in the base token set.
const modeTokens = {
    light: {
        colorBgContainer: 'rgba(255, 255, 255, 0.62)',
        colorBgElevated: 'rgba(255, 255, 255, 0.82)',
        colorBgMask: 'rgba(16, 24, 40, 0.28)',
        colorBorder: 'rgba(255, 255, 255, 0.55)',
        colorBorderSecondary: 'rgba(15, 23, 42, 0.07)',
        colorText: '#344054',
        colorTextHeading: '#101828',
        colorTextSecondary: '#667085',
        colorTextTertiary: '#98a2b3',
        colorTextPlaceholder: '#98a2b3',
        boxShadow: '0 8px 28px rgba(31, 38, 135, 0.09)',
        boxShadowSecondary: '0 16px 48px rgba(31, 38, 135, 0.12)',
    },
    dark: {
        colorBgContainer: 'rgba(30, 32, 44, 0.72)',
        colorBgElevated: 'rgba(24, 26, 36, 0.92)',
        colorBgMask: 'rgba(0, 0, 0, 0.45)',
        colorBorder: 'rgba(255, 255, 255, 0.12)',
        colorBorderSecondary: 'rgba(255, 255, 255, 0.08)',
        colorText: '#cbd2e1',
        colorTextHeading: '#f2f4f8',
        colorTextSecondary: '#94a0b8',
        colorTextTertiary: '#6b7488',
        colorTextPlaceholder: '#6b7488',
        boxShadow: '0 8px 28px rgba(0, 0, 0, 0.35)',
        boxShadowSecondary: '0 16px 48px rgba(0, 0, 0, 0.40)',
    },
};

/** Builds the ConfigProvider theme for "light" or "dark". Keep in step with assets/glass/_tokens.scss. */
export const getAntdTheme = (mode = 'light') => ({
    algorithm: mode === 'dark' ? antdThemeAlgorithms.darkAlgorithm : undefined,
    token: {
        fontFamily: 'iranyekan',

        // Brand
        colorPrimary: BRAND,
        colorInfo: '#2e90fa',
        colorSuccess: '#12b76a',
        colorWarning: '#f79009',
        colorError: '#f04438',
        colorLink: BRAND,

        // Geometry — matches --radius-* in _tokens.scss
        borderRadius: 14,
        borderRadiusLG: 18,
        borderRadiusSM: 10,
        borderRadiusXS: 8,

        controlHeight: 38,
        wireframe: false,
        motionDurationMid: '0.25s',
        motionEaseInOut: 'cubic-bezier(0.22, 1, 0.36, 1)',

        ...modeTokens[mode],
    },

    components: {
        Layout: {
            headerBg: 'transparent',
            bodyBg: 'transparent',
            siderBg: 'transparent',
            footerBg: 'transparent',
        },
        Card: {
            colorBgContainer: 'var(--glass-subtle)',
            headerBg: 'transparent',
            paddingLG: 20,
        },
        Table: {
            // Transparent rows let the frosted table shell show through; the
            // stylesheet supplies the header gradient and hover tint.
            colorBgContainer: 'transparent',
            headerBg: 'transparent',
            headerColor: 'var(--text-strong)',
            rowHoverBg: 'rgba(var(--brand-rgb), 0.10)',
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.055)',
            headerSplitColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.10)' : 'rgba(15, 23, 42, 0.08)',
        },
        Modal: {
            contentBg: 'transparent',
            headerBg: 'transparent',
            footerBg: 'transparent',
            titleColor: 'var(--text-strong)',
        },
        Drawer: {
            colorBgElevated: 'transparent',
        },
        Dropdown: {
            colorBgElevated: 'transparent',
        },
        Select: {
            colorBgElevated: 'transparent',
            optionSelectedBg: 'transparent',
        },
        Input: {
            colorBgContainer: 'var(--glass-subtle)',
            activeBorderColor: 'rgba(var(--brand-rgb), 0.55)',
            hoverBorderColor: 'rgba(var(--brand-rgb), 0.32)',
            activeShadow: '0 0 0 4px rgba(var(--brand-rgb), 0.12)',
        },
        InputNumber: {
            colorBgContainer: 'var(--glass-subtle)',
        },
        DatePicker: {
            colorBgContainer: 'var(--glass-subtle)',
            colorBgElevated: 'transparent',
        },
        Button: {
            primaryShadow: 'none',
            defaultShadow: 'none',
            dangerShadow: 'none',
            fontWeight: 600,
        },
        Tabs: {
            itemSelectedColor: BRAND,
            itemHoverColor: BRAND,
            inkBarColor: BRAND,
            cardBg: 'transparent',
        },
        Collapse: {
            headerBg: 'transparent',
            contentBg: 'transparent',
        },
        Descriptions: {
            labelBg: 'rgba(var(--brand-rgb), 0.07)',
            colorSplit: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.45)',
        },
        Menu: {
            itemBg: 'transparent',
            subMenuItemBg: 'transparent',
            itemSelectedBg: 'transparent',
            itemSelectedColor: '#ffffff',
        },
        Tooltip: {
            colorBgSpotlight: 'rgba(16, 24, 40, 0.86)',
        },
        Message: {
            contentBg: 'transparent',
        },
        Notification: {
            colorBgElevated: 'transparent',
        },
        Segmented: {
            itemSelectedBg: 'var(--glass-raised)',
        },
        Pagination: {
            itemActiveBg: 'transparent',
        },
        List: {
            colorBgContainer: 'transparent',
        },
        Spin: {
            colorPrimary: BRAND,
        },
    },
});

export default getAntdTheme('light');
